/**
 * 스케줄 공지용 제약사 선택 모달 (전체/그 외/개별).
 * 레거시 `PharmaCompanySelectModal.js` 포팅 — RDS Pop-Up/Checkbox 적용.
 */
import { usePharmaCompanyList } from '@/entities/pharma/hooks/usePharmaCompanies';
import {
  PHARMA_SELECTION_SPECIAL_OPTIONS,
  PHARMA_SELECTION_TYPE,
  type PharmaSelectionType,
  isSamePharmaCompanyId,
} from '@/pages/board/announcementSchedulePharma';
import type { PharmaCompanyOption } from '@/pages/board/PharmaCompanySelectModal';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  popupPrimaryButtonClassName,
  popupSecondaryButtonClassName,
} from '@/shared/components/ui/popup-styles';
import { FilterRow } from '@/shared/components/ui/row';
import { cn } from '@/shared/lib/utils';
import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

export interface SchedulePharmaSelection {
  pharmaSelectionType: PharmaSelectionType;
  pharmaCompanies: PharmaCompanyOption[];
}

export interface SchedulePharmaSelectionConstraints {
  hasAllPharmaElsewhere?: boolean;
  hasOtherPharmaElsewhere?: boolean;
  hasIndividualPharmaElsewhere?: boolean;
  selectedIndividualPharmaIdsElsewhere?: Array<string | number>;
}

interface SchedulePharmaSelectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSelection?: Partial<SchedulePharmaSelection>;
  selectionConstraints?: SchedulePharmaSelectionConstraints;
  onConfirm: (selection: SchedulePharmaSelection) => void;
}

const normalizeInitialSelection = (
  initialSelection: Partial<SchedulePharmaSelection> = {},
): SchedulePharmaSelection => ({
  pharmaSelectionType:
    initialSelection.pharmaSelectionType || PHARMA_SELECTION_TYPE.SELECTED,
  pharmaCompanies: (initialSelection.pharmaCompanies || []).map((company) => ({
    pharmaCompanyId: company.pharmaCompanyId,
    pharmaCompanyName: company.pharmaCompanyName,
  })),
});

const listItemClassName = (disabled: boolean) =>
  cn(
    'flex items-center gap-3 border-b border-ruda-border-light px-4 py-3 last:border-b-0',
    disabled
      ? 'cursor-not-allowed text-ruda-text-tertiary'
      : 'cursor-pointer text-ruda-text-primary hover:bg-ruda-grey-50',
  );

export const SchedulePharmaSelectModal: React.FC<
  SchedulePharmaSelectModalProps
> = ({
  open,
  onOpenChange,
  initialSelection = {},
  selectionConstraints = {},
  onConfirm,
}) => {
  const [keyword, setKeyword] = useState('');
  /** 실제 조회 키워드 — 검색 버튼/엔터 시에만 반영 */
  const [appliedKeyword, setAppliedKeyword] = useState('');
  const [tempSelectionType, setTempSelectionType] = useState<PharmaSelectionType>(
    PHARMA_SELECTION_TYPE.SELECTED,
  );
  const [tempSelectedCompanies, setTempSelectedCompanies] = useState<
    PharmaCompanyOption[]
  >([]);

  const {
    hasAllPharmaElsewhere = false,
    hasOtherPharmaElsewhere = false,
    hasIndividualPharmaElsewhere = false,
    selectedIndividualPharmaIdsElsewhere = [],
  } = selectionConstraints;

  const selectedIndividualPharmaIdsElsewhereSet = useMemo(
    () => new Set(selectedIndividualPharmaIdsElsewhere.map(String)),
    [selectedIndividualPharmaIdsElsewhere],
  );

  const selectedCompanyIdSet = useMemo(
    () =>
      new Set(tempSelectedCompanies.map((item) => String(item.pharmaCompanyId))),
    [tempSelectedCompanies],
  );

  const disableIndividualCompanies =
    hasAllPharmaElsewhere || tempSelectionType === PHARMA_SELECTION_TYPE.ALL;
  const disableAllPharmaOption =
    hasAllPharmaElsewhere ||
    hasOtherPharmaElsewhere ||
    hasIndividualPharmaElsewhere ||
    (tempSelectionType === PHARMA_SELECTION_TYPE.SELECTED &&
      tempSelectedCompanies.length > 0);
  const disableOtherPharmaOption =
    hasAllPharmaElsewhere ||
    tempSelectionType === PHARMA_SELECTION_TYPE.ALL ||
    hasOtherPharmaElsewhere;

  const isIndividualCompanyDisabled = (companyId: number) =>
    disableIndividualCompanies ||
    selectedIndividualPharmaIdsElsewhereSet.has(String(companyId));

  const { data, isLoading, refetch } = usePharmaCompanyList({
    search: {
      pharmaCompanyNameSearchKey: appliedKeyword.trim() || undefined,
    },
    pageable: { page: 0, size: 100 },
  });

  const companies = data?.content ?? [];

  useEffect(() => {
    if (!open) return;
    setKeyword('');
    setAppliedKeyword('');
    const normalized = normalizeInitialSelection(initialSelection);
    setTempSelectionType(normalized.pharmaSelectionType);
    setTempSelectedCompanies(normalized.pharmaCompanies);
    void refetch();
  }, [open, initialSelection, refetch]);

  const handleSearch = () => {
    setAppliedKeyword(keyword.trim());
  };

  const toggleSpecialSelection = (selectionType: PharmaSelectionType) => {
    if (selectionType === PHARMA_SELECTION_TYPE.ALL) {
      if (
        disableAllPharmaOption &&
        tempSelectionType !== PHARMA_SELECTION_TYPE.ALL
      ) {
        return;
      }
      if (tempSelectionType === PHARMA_SELECTION_TYPE.ALL) {
        setTempSelectionType(PHARMA_SELECTION_TYPE.SELECTED);
        setTempSelectedCompanies([]);
        return;
      }
      setTempSelectionType(PHARMA_SELECTION_TYPE.ALL);
      setTempSelectedCompanies([]);
      return;
    }

    if (selectionType === PHARMA_SELECTION_TYPE.OTHERS) {
      if (
        disableOtherPharmaOption &&
        tempSelectionType !== PHARMA_SELECTION_TYPE.OTHERS
      ) {
        return;
      }
      if (tempSelectionType === PHARMA_SELECTION_TYPE.OTHERS) {
        setTempSelectionType(PHARMA_SELECTION_TYPE.SELECTED);
        setTempSelectedCompanies([]);
        return;
      }
      setTempSelectionType(PHARMA_SELECTION_TYPE.OTHERS);
      setTempSelectedCompanies([]);
    }
  };

  const toggleIndividualCompany = (company: PharmaCompanyOption) => {
    const companyId = String(company.pharmaCompanyId);

    setTempSelectedCompanies((prev) => {
      const exists = prev.some((item) =>
        isSamePharmaCompanyId(item.pharmaCompanyId, companyId),
      );

      if (!exists && isIndividualCompanyDisabled(company.pharmaCompanyId)) {
        return prev;
      }

      if (exists) {
        const next = prev.filter(
          (item) => !isSamePharmaCompanyId(item.pharmaCompanyId, companyId),
        );
        if (next.length === 0) {
          setTempSelectionType(PHARMA_SELECTION_TYPE.SELECTED);
        }
        return next;
      }

      setTempSelectionType(PHARMA_SELECTION_TYPE.SELECTED);
      return [...prev, company];
    });
  };

  const handleConfirm = () => {
    onConfirm({
      pharmaSelectionType: tempSelectionType,
      pharmaCompanies:
        tempSelectionType === PHARMA_SELECTION_TYPE.SELECTED
          ? tempSelectedCompanies
          : [],
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-6 overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>제약사 선택</DialogTitle>
          <DialogDescription>
            전체·그 외 또는 개별 제약사를 선택할 수 있습니다.
          </DialogDescription>
        </DialogHeader>

        <FilterRow
          gap={12}
          className="w-full shrink-0"
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <Field
            label="제약사명 검색"
            htmlFor="schedule-pharma-search"
            className="min-w-0 flex-1"
          >
            <Input
              id="schedule-pharma-search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="제약사명 검색"
              className="w-full"
            />
          </Field>
          <Button
            type="submit"
            className="ruda-button shrink-0 self-end"
            disabled={isLoading}
          >
            <Search className="mr-2 h-4 w-4" />
            검색
          </Button>
        </FilterRow>

        <div className="min-h-0 flex-1 overflow-y-auto rounded-12 border border-ruda-border-light bg-white">
          {PHARMA_SELECTION_SPECIAL_OPTIONS.map((specialOption) => {
            const selectionType = specialOption.pharmaSelectionType;
            const isAllPharma = selectionType === PHARMA_SELECTION_TYPE.ALL;
            const isOtherPharma = selectionType === PHARMA_SELECTION_TYPE.OTHERS;
            const disabled =
              (isAllPharma && disableAllPharmaOption) ||
              (isOtherPharma && disableOtherPharmaOption);
            const checked = tempSelectionType === selectionType;

            return (
              <label
                key={selectionType}
                className={listItemClassName(disabled)}
              >
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={() => toggleSpecialSelection(selectionType)}
                  aria-label={specialOption.label}
                />
                <span className="text-sm font-normal leading-[22px] tracking-tight">
                  {specialOption.label}
                </span>
              </label>
            );
          })}

          {isLoading ? (
            <div className="flex min-h-[160px] items-center justify-center py-8">
              <LoadingSpinner tip="제약사 목록을 불러오는 중..." />
            </div>
          ) : companies.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm tracking-tight text-ruda-text-tertiary">
              검색 결과가 없습니다.
            </p>
          ) : (
            companies.map((company) => {
              const option: PharmaCompanyOption = {
                pharmaCompanyId: company.pharmaCompanyId,
                pharmaCompanyName: company.pharmaCompanyName,
              };
              const companyId = String(company.pharmaCompanyId);
              const disabled = isIndividualCompanyDisabled(
                company.pharmaCompanyId,
              );
              const checked = selectedCompanyIdSet.has(companyId);

              return (
                <label
                  key={companyId}
                  className={listItemClassName(disabled)}
                >
                  <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={() => toggleIndividualCompany(option)}
                    aria-label={option.pharmaCompanyName}
                  />
                  <span className="text-sm font-normal leading-[22px] tracking-tight">
                    {option.pharmaCompanyName}
                  </span>
                </label>
              );
            })
          )}
        </div>

        <DialogFooter className="shrink-0">
          <Button
            type="button"
            className={popupSecondaryButtonClassName}
            onClick={() => onOpenChange(false)}
          >
            취소
          </Button>
          <Button
            type="button"
            className={popupPrimaryButtonClassName}
            onClick={handleConfirm}
          >
            선택완료
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
